import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import sessionManager from '../utils/sessionManager';
import API_BASE_URL from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const refreshInFlightRef = useRef(null);

  const token = sessionManager.getSession()?.token;

  // Configure axios interceptor for automatic token inclusion
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const session = sessionManager.getSession();
        if (session?.token && !config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${session.token}`;
          // Update activity on each request
          sessionManager.updateActivity();
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error.response?.status;
        const originalRequest = error.config;

        // Auto-refresh on 401 if we have a refresh token and haven't retried yet
        if (status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          const session = sessionManager.getSession();
          if (session?.refreshToken) {
            try {
              if (!refreshInFlightRef.current) {
                refreshInFlightRef.current = axios
                  .post(`${API_BASE_URL}/auth/refresh`, { refreshToken: session.refreshToken })
                  .finally(() => {
                    refreshInFlightRef.current = null;
                  });
              }

              const refreshRes = await refreshInFlightRef.current;
              if (refreshRes.data.success && refreshRes.data.token) {
                const { token: newToken, refreshToken: newRefreshToken, user: freshUser } = refreshRes.data;
                const normalizedUser = {
                  ...freshUser,
                  _id: freshUser.id || freshUser._id,
                  id: freshUser.id || freshUser._id,
                  role: freshUser.role
                };
                sessionManager.setSession(normalizedUser, newToken, newRefreshToken);
                setUser(normalizedUser);
                setIsAuthenticated(true);
                sessionManager.broadcastSessionChange('token_refreshed', { userId: normalizedUser.id || normalizedUser._id });
                // Retry original request with new token
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return axios(originalRequest);
              }
            } catch (refreshErr) {
              const latest = sessionManager.getSession();
              if (latest?.token && latest.token !== session.token) {
                originalRequest.headers.Authorization = `Bearer ${latest.token}`;
                return axios(originalRequest);
              }

              const refreshStatus = refreshErr?.response?.status;
              if (!refreshStatus) {
                return Promise.reject(error);
              }

              console.log('🚫 AuthContext: Token refresh failed, logging out');
              sessionManager.clearSession();
              sessionManager.broadcastSessionChange('logout');
              setUser(null);
              setIsAuthenticated(false);
              if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
                window.location.href = '/login';
              }
              return Promise.reject(refreshErr);
            }
          }
          // No refresh token available — clear session
          sessionManager.clearSession();
          sessionManager.broadcastSessionChange('logout');
          setUser(null);
          setIsAuthenticated(false);
          if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }

        if (status === 403) {
          const code = error.response?.data?.error;
          if (code === 'KYC_REQUIRED') {
            try {
              const path = window.location.pathname || '/';
              sessionStorage.setItem('kyc_return_to', path);
            } catch (e) {
            }

            if (window.location.pathname !== '/verification') {
              window.location.href = '/verification';
            }
          }
        }

        // Lockdown handling: when backend returns 503 with the lockdown message,
        // keep the session but force the user onto the Lockdown Access page so
        // they can enter the shared password.
        if (status === 503) {
          const msg = error.response?.data?.error || '';
          if (msg.includes('Platform is currently locked for testing')) {
            if (window.location.pathname !== '/lockdown-access') {
              console.log('🔒 AuthContext: Lockdown active, redirecting to /lockdown-access');
              window.location.href = '/lockdown-access';
            }
          }
          return Promise.reject(error);
        }

        // For retried requests that still fail with 401, just reject
        // without clearing session again (already handled above).
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      console.log('🔍 AuthContext: Starting authentication check...');
      
      // Use SessionManager to get session
      const session = sessionManager.getSession();
      
      if (session) {
        console.log('✅ AuthContext: Valid session found:', sessionManager.getSessionInfo());
        
        // Set user immediately from session to prevent flash
        setUser(session.user);
        setIsAuthenticated(true);
        console.log('✅ AuthContext: Set user from session immediately');
        
        // Verify session is still valid with server in background
        try {
          console.log('🔍 AuthContext: Verifying session with server...');
          const response = await axios.get(
            `${API_BASE_URL}/auth/me`,
            {
              headers: { Authorization: `Bearer ${session.token}` },
              timeout: 10000 // 10 second timeout
            }
          );
          
          if (response.data.success && response.data.user) {
            // Normalize user object
            const normalizedUser = {
              ...response.data.user,
              _id: response.data.user.id || response.data.user._id,
              id: response.data.user.id || response.data.user._id,
              role: response.data.user.role || session.user.role
            };
            
            console.log('✅ AuthContext: Session validated with server, user role:', normalizedUser.role);
            setUser(normalizedUser);
            setIsAuthenticated(true);
            
            // Update session with fresh data
            sessionManager.setSession(normalizedUser, session.token);
          } else {
            console.log('❌ AuthContext: Invalid session response from server');
            sessionManager.clearSession();
            setUser(null);
            setIsAuthenticated(false);
          }
        } catch (error) {
          console.error('❌ Session validation failed:', error);
          
          if (error.response?.status === 401) {
            console.log('❌ AuthContext: 401 error, clearing session');
            sessionManager.clearSession();
            setUser(null);
            setIsAuthenticated(false);
          } else if (error.code === 'ECONNABORTED' || error.code === 'NETWORK_ERROR') {
            console.log('⚠️ AuthContext: Network error, keeping stored session');
            // Keep the session user for offline functionality
            setUser(session.user);
            setIsAuthenticated(true);
          } else {
            console.log('❌ AuthContext: Unknown error, clearing session');
            sessionManager.clearSession();
            setUser(null);
            setIsAuthenticated(false);
          }
        }
      } else {
        console.log('❌ AuthContext: No valid session found');
        setUser(null);
        setIsAuthenticated(false);
      }
      
      console.log('🏁 AuthContext: Authentication check complete');
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  // Listen for session changes across tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      console.log('🔄 AuthContext: Storage change detected:', e.key);
      
      // Check for session-related changes
      if (e.key === 'token' || e.key === 'user' || e.key === 'role' || e.key === 'session_event') {
        const session = sessionManager.getSession();
        
        if (session) {
          console.log('✅ AuthContext: Syncing session from storage:', session.user.role);
          setUser(session.user);
          setIsAuthenticated(true);
        } else {
          console.log('❌ AuthContext: No valid session, logging out');
          setUser(null);
          setIsAuthenticated(false);
        }
      }
    };

    const handleSessionChange = (e) => {
      console.log('🔄 AuthContext: Session change event:', e.detail);
      
      const session = sessionManager.getSession();
      if (session) {
        setUser(session.user);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('sessionChange', handleSessionChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('sessionChange', handleSessionChange);
    };
  }, []);

  const login = (userData, token, refreshToken) => {
    // Normalize user object with consistent ID field
    const normalizedUser = {
      ...userData,
      _id: userData.id || userData._id,
      id: userData.id || userData._id,
      role: userData.role // Ensure role is explicitly set
    };
    
    console.log('✅ AuthContext: Logging in user with role:', normalizedUser.role);
    
    // Use SessionManager to set session
    if (sessionManager.setSession(normalizedUser, token, refreshToken)) {
      setUser(normalizedUser);
      setIsAuthenticated(true);
      
      // Broadcast login to other tabs
      sessionManager.broadcastSessionChange('login', normalizedUser);
      console.log('✅ AuthContext: Login successful and broadcasted');
    } else {
      console.error('❌ AuthContext: Failed to set session');
    }
  };

  const register = async (userData) => {
    try {
      console.log('AuthContext: Sending registration data:', userData);
      const response = await axios.post(
        `${API_BASE_URL}/auth/register`,
        userData
      );

      console.log('AuthContext: Registration response:', response.data);

      if (response.data.success) {
        const normalizedUser = {
          ...response.data.user,
          _id: response.data.user.id || response.data.user._id,
          id: response.data.user.id || response.data.user._id
        };
        
        console.log('AuthContext: Setting user:', normalizedUser);
        
        // Use SessionManager for registration too
        if (sessionManager.setSession(normalizedUser, response.data.token, response.data.refreshToken)) {
          setUser(normalizedUser);
          setIsAuthenticated(true);
          sessionManager.broadcastSessionChange('register', normalizedUser);
        }
        
        return response.data;
      }
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      const session = sessionManager.getSession();
      if (session?.token) {
        await axios.post(
          `${API_BASE_URL}/auth/logout`,
          { refreshToken: session.refreshToken },
          {
            headers: { Authorization: `Bearer ${session.token}` }
          }
        );
      }
    } catch (error) {
      console.error('AuthContext: Logout request failed:', error);
    }

    sessionManager.clearSession();
    sessionManager.broadcastSessionChange('logout');

    setUser(null);
    setIsAuthenticated(false);

    try {
      sessionStorage.removeItem('linkdash_home_redirected');
    } catch (e) {
      // ignore
    }

    window.location.href = '/login';
  };

  // Function to refresh user session
  const refreshSession = async () => {
    const session = sessionManager.getSession();
    if (!session?.token) return false;
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}/auth/me`,
        {
          headers: { Authorization: `Bearer ${session.token}` }
        }
      );
      
      if (response.data.success && response.data.user) {
        const normalizedUser = {
          ...response.data.user,
          _id: response.data.user.id || response.data.user._id,
          id: response.data.user.id || response.data.user._id,
          role: response.data.user.role
        };
        
        setUser(normalizedUser);
        setIsAuthenticated(true);
        sessionManager.setSession(normalizedUser, session.token);
        sessionManager.broadcastSessionChange('refresh', normalizedUser);
        return true;
      }
    } catch (error) {
      console.error('Session refresh failed:', error);
      return false;
    }
    return false;
  };

  const updateUser = (partialUser) => {
    setUser((prev) => {
      const base = prev || {};
      const merged = { ...base, ...partialUser };

      const session = sessionManager.getSession();
      if (session?.token) {
        const normalized = {
          ...merged,
          _id: merged.id || merged._id || base._id,
          id: merged.id || merged._id || base.id,
          role: merged.role || base.role || session.role,
        };

        sessionManager.setSession(normalized, session.token);
        sessionManager.broadcastSessionChange('refresh', normalized);
        return normalized;
      }

      return merged;
    });
  };

  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    refreshSession,
    updateUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
